import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { siteData } from '../data.js';
import SEO from '../components/SEO.jsx';
import { useCmsCollection } from '../lib/cms.js';
import { Calendar, MapPin, Tag, ArrowRight, X, Play, Video, ExternalLink, Heart, Sparkles, CheckCircle, Users, Download } from 'lucide-react';

export default function Events() {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const { data: rawEvents } = useCmsCollection('events', siteData.events || []);

  const fallbackEvents = siteData.events || [];
  const eventsList = rawEvents.map((evt, idx) => {
    const d = evt.data || evt;
    const fallback = fallbackEvents[idx] || fallbackEvents[0] || {};
    return {
      id: d.id || fallback.id || `event-${idx}`,
      title: d.titel || d.title || fallback.title,
      shortTitle: d.kort_titel || d.shortTitle || fallback.shortTitle,
      location: d.lokation || d.location || fallback.location,
      date: d.dato || d.date || fallback.date,
      category: d.kategori || fallback.category,
      status: d.status_tekst || d.status || fallback.status,
      image: Array.isArray(d.billede) ? d.billede[0] : (d.billede || fallback.image),
      excerpt: d.uddrag || d.excerpt || fallback.excerpt,
      content: d.indhold || d.content || fallback.content,
      videos: fallback.videos || [],
    };
  });

  return (
    <div className="bg-slate-50 min-h-screen pt-32 pb-24">
      <SEO
        title="Events & Arrangementer | Fællesskab og velgørenhed"
        description="Følg med i kommende og afholdte events, velgørenhedsaftener og foredrag arrangeret af Tak For Din Indsats til støtte for Danmarks frontpersonale."
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Navigation Tabs (Seamless connection between News and Events) */}
        <div className="flex justify-center mb-12">
          <div className="bg-white p-1.5 rounded-2xl shadow-sm border border-slate-200/80 flex gap-1">
            <Link 
              to="/nyheder" 
              className="px-6 py-2.5 rounded-xl text-sm font-bold text-slate-500 hover:text-slate-900 transition-all"
            >
              Nyheder
            </Link>
            <span className="px-6 py-2.5 rounded-xl text-sm font-bold bg-red-600 text-white shadow-md shadow-red-600/20">
              Events
            </span>
          </div>
        </div>

        {/* Header Section */}
        <div className="max-w-3xl mx-auto text-center mb-14">
          <span className="text-red-600 font-bold tracking-wider uppercase text-xs sm:text-sm bg-red-50 border border-red-100 px-4 py-1.5 rounded-full inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            Arrangementer & Aktiviteter
          </span>
          <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 mt-4 mb-5 tracking-tight">
            Nyheder / Events
          </h1>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
            Se foreningens afholdte og kommende velgørenhedsarrangementer, samlinger og videoklip fra vores aktiviteter.
          </p>
        </div>

        {/* Featured Event: Velgørenhedsaften 10. september 2026 */}
        {eventsList.map((event) => (
          <div key={event.id} className="mb-16">
            <div className="bg-white rounded-[2.5rem] overflow-hidden border border-slate-200/70 shadow-xl shadow-slate-200/50">
              
              {/* Event Header Banner */}
              <div className="p-6 sm:p-8 md:p-12 border-b border-slate-100 bg-gradient-to-br from-white via-slate-50/50 to-red-50/20">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="bg-red-600 text-white text-xs font-bold uppercase tracking-wider px-3.5 py-1.5 rounded-xl shadow-sm">
                      {event.category}
                    </span>
                    <span className="bg-slate-100 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-xl border border-slate-200">
                      {event.status}
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                    <span className="flex items-center gap-1.5 font-medium">
                      <Calendar className="w-4 h-4 text-red-600 shrink-0" />
                      {event.date}
                    </span>
                    <span className="flex items-center gap-1.5 font-medium">
                      <MapPin className="w-4 h-4 text-red-600 shrink-0" />
                      {event.location}
                    </span>
                  </div>
                </div>

                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight leading-snug">
                  {event.title}
                </h2>

                <p className="text-base sm:text-lg text-slate-700 leading-relaxed max-w-4xl font-normal">
                  {event.content}
                </p>

                <div className="mt-5 inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-red-700 bg-red-50/80 px-4 py-2 rounded-xl border border-red-100">
                  <Heart className="w-4 h-4 text-red-600 shrink-0 fill-red-600" />
                  Alle indkomne beløb går ubeskåret til foreningens aktiviteter for Danmarks frontpersonale.
                </div>
              </div>

              {/* Video Section */}
              {event.videos && event.videos.length > 0 && (
                <div className="p-6 sm:p-8 md:p-12 bg-slate-900 text-white">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                    <div>
                      <span className="text-red-400 font-bold tracking-wider uppercase text-xs">
                        Videoklip fra aftenen
                      </span>
                      <h3 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-white mt-1">
                        Se videoer fra Velgørenhedsaftenen
                      </h3>
                    </div>
                    <p className="text-slate-400 text-sm max-w-md">
                      Her kan du se optagelser og stemningsklip fra Utzon Center i Aalborg.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {event.videos.map((vid) => (
                      <div 
                        key={vid.id}
                        className="bg-slate-800/90 rounded-3xl p-5 sm:p-6 border border-slate-700 shadow-xl flex flex-col justify-between"
                      >
                        <div>
                          {/* Video player container */}
                          <div className="relative aspect-video w-full rounded-2xl overflow-hidden bg-black mb-5 shadow-2xl border border-slate-700/60">
                            <video
                              controls
                              playsInline
                              preload="metadata"
                              className="w-full h-full object-contain"
                            >
                              <source src={vid.src} type={vid.type} />
                              {vid.type !== 'video/mp4' && (
                                <source src={vid.src} type="video/mp4" />
                              )}
                              Din browser understøtter ikke videoafspilning direkte i dette format.
                            </video>
                          </div>

                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider bg-red-600 text-white">
                              {vid.badge}
                            </span>
                            <span className="text-xs text-slate-400">
                              {vid.subtitle}
                            </span>
                          </div>

                          <h4 className="text-lg sm:text-xl font-bold text-white mb-2">
                            {vid.title}
                          </h4>
                          <p className="text-sm text-slate-300 leading-relaxed mb-4">
                            {vid.description || "Optagelse fra TFDI velgørenhedsaften på Utzon Center i Aalborg."}
                          </p>
                        </div>

                        <div className="pt-4 border-t border-slate-700/60 flex items-center justify-between">
                          <a
                            href={vid.src}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            className="inline-flex items-center gap-2 text-xs font-semibold text-slate-300 hover:text-red-400 transition-colors"
                          >
                            <Download className="w-3.5 h-3.5" />
                            <span>Download video ({vid.badge})</span>
                          </a>
                          <span className="text-[11px] text-slate-500">
                            TFDI • Utzon Center
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Event Footer / Additional Information */}
              <div className="p-6 sm:p-8 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-slate-600 text-center sm:text-left">
                  Vil du støtte foreningens arbejde eller høre mere om kommende events?
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    to="/stoet-os"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-xl transition-all shadow-md shadow-red-600/10"
                  >
                    Støt vores arbejde
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-white hover:bg-slate-100 text-slate-800 text-sm font-bold rounded-xl border border-slate-200 transition-all"
                  >
                    Kontakt os
                  </Link>
                </div>
              </div>

            </div>
          </div>
        ))}

        {/* Informative Box about Upcoming Events */}
        <div className="bg-white rounded-3xl p-8 sm:p-10 border border-slate-200/70 text-center max-w-3xl mx-auto shadow-sm">
          <div className="w-14 h-14 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-7 h-7" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">
            Flere arrangementer på vej
          </h3>
          <p className="text-slate-600 text-sm sm:text-base max-w-xl mx-auto leading-relaxed mb-6">
            Vi opdaterer løbende siden her med nye velgørenhedsarrangementer, netværksdage og sammenkomster. Følg også gerne vores sociale medier for hurtige opdateringer.
          </p>
          <div className="flex justify-center gap-3">
            <a
              href={siteData.socials.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs sm:text-sm font-semibold transition-colors"
            >
              Følg på Facebook
            </a>
            <a
              href={siteData.socials.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs sm:text-sm font-semibold transition-colors"
            >
              Følg på Instagram
            </a>
          </div>
        </div>

      </div>
    </div>
  );
}
